import { Injectable } from "@angular/core";
import { Subject } from "rxjs";
import { AppColor, appDarkColors, appLightColors } from "src/constants/colors";
import { LocalStorageService } from "./local-storage.service";

export interface Theme {
    type: ThemeStyle,
    colors: { key: AppColor, value: string }[],
}

export enum ThemeStyle {
    light, dark, system
}

@Injectable({ providedIn: 'root' })
export class ThemeService {
    static readonly THEME_STORAGE_KEY = 'theme';

    private readonly _themes: Theme[];
    public get themes() {
        return this._themes;
    }

    private _currentTheme: ThemeStyle;

    private _themeChanged = new Subject<Theme>();
    themeChanged$ = this._themeChanged.asObservable();

    private themeMediaQuery: MediaQueryList;
    private themeChangeHandler: (event: MediaQueryListEvent) => Promise<void>;

    private _current: ThemeStyle;
    get current() {
        return this._current;
    }

    constructor(
        private localStorageService: LocalStorageService,
    ) {
        this._themes = [
            {
                type: ThemeStyle.light,
                colors: appLightColors,
            },
            {
                type: ThemeStyle.dark,
                colors: appDarkColors,
            }
        ];
    }

    async changeTo(type: ThemeStyle) {
        const theme = type === ThemeStyle.system ? this._themes[this.getSystemTheme()] : this._themes[type];

        this._currentTheme = type;
        this._current = type === ThemeStyle.system ? this.getSystemTheme() : type;

        document.getElementsByTagName('body')[0].style.background = this.current === ThemeStyle.light ? 'var(--background-image)' : 'var(--bg)';

        for (let i = 0; i < theme.colors.length; i++) {
            const colorData = theme.colors[i];
            const cssVariableName = `--${colorData.key.replace(/_/g, '-')}`;

            document.documentElement.style.setProperty(cssVariableName, colorData.value);
        }

        await this.localStorageService.set(ThemeService.THEME_STORAGE_KEY, type);

        this._themeChanged.next(theme);
    }

    changeStylePropertyValue(property: AppColor, value: string) {
        const cssVariableName = `--${property.replace(/_/g, '-')}`;
        document.documentElement.style.setProperty(cssVariableName, value);
    }

    getSystemTheme(): ThemeStyle {
        return (window.matchMedia('(prefers-color-scheme: dark)').matches ? ThemeStyle.dark : ThemeStyle.light);
    }

    async getStorageTheme(): Promise<ThemeStyle> {
        return (await this.localStorageService.get(ThemeService.THEME_STORAGE_KEY) as ThemeStyle) ?? ThemeStyle.light;
    }

    subscribeSystemThemeChange() {
        this.themeMediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

        this.themeChangeHandler = async (event: MediaQueryListEvent) => {
            const storageTheme = await this.getStorageTheme();
            if (storageTheme === ThemeStyle.system) {
                await this.changeTo(ThemeStyle.system);
            }
            else {
                await this.changeTo(event.matches ? ThemeStyle.dark : ThemeStyle.light);
            }
        };

        this.themeMediaQuery.addEventListener('change', this.themeChangeHandler);
    }

    unsubscribeSystemThemeChange() {
        if (this.themeMediaQuery && this.themeChangeHandler) {
            this.themeMediaQuery.removeEventListener('change', this.themeChangeHandler);
        }
    }

    getCurrentThemeData(): Theme {
        return this._themes[this._currentTheme === ThemeStyle.system ? this.getSystemTheme() : this._currentTheme];
    }

    async purge() {
        await this.localStorageService.remove(ThemeService.THEME_STORAGE_KEY);
    }
}